import { useState } from 'react'
import { App, Button, Card, Popconfirm, Select, Space, Table, Upload } from 'antd'
import { DeleteOutlined, PaperClipOutlined, UploadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDateTime } from '@/shared/lib/format'
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_KINDS,
  ATTACHMENT_MAX_BYTES,
  attachmentUrl,
  deleteAttachment,
  fetchAttachments,
  uploadAttachment,
  type AttachmentKind,
} from './stakeholderApi'

/** Shartnoma, ulush kelishuvi, shaxsni tasdiqlovchi hujjat va boshqa asoslar — fayl xsib-file da. */
export function StakeholderAttachmentsCard({ stakeholderId }: { stakeholderId: string }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<AttachmentKind>('AGREEMENT')

  const attachments = useQuery({
    queryKey: queryKeys.stakeholder.attachments(stakeholderId),
    queryFn: ({ signal }) => fetchAttachments(stakeholderId, signal),
  })

  const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.attachments(stakeholderId) })

  const upload = useMutation({
    mutationFn: (file: File) => uploadAttachment(stakeholderId, kind, file),
    onSuccess: refresh,
    onError: (error) => void message.error(translateError(error, t)),
  })

  const remove = useMutation({
    mutationFn: (attachmentId: string) => deleteAttachment(stakeholderId, attachmentId),
    onSuccess: refresh,
    onError: (error) => void message.error(translateError(error, t)),
  })

  const open = async (attachmentId: string) => {
    try {
      window.open(await attachmentUrl(stakeholderId, attachmentId), '_blank', 'noopener')
    } catch (error) {
      void message.error(translateError(error, t))
    }
  }

  return (
    <Card
      title={t('stakeholder.attachments.title')}
      extra={
        <Can permission="stakeholder.manage">
          <Space wrap>
            <Select<AttachmentKind>
              value={kind}
              onChange={setKind}
              style={{ minWidth: 180 }}
              options={ATTACHMENT_KINDS.map((value) => ({ value, label: t(`stakeholder.attachmentKind.${value}`) }))}
            />
            <Upload
              accept={ATTACHMENT_ACCEPT}
              showUploadList={false}
              beforeUpload={(file) => {
                if (file.size > ATTACHMENT_MAX_BYTES) {
                  void message.warning(t('stakeholder.attachments.tooLarge'))
                  return false
                }
                upload.mutate(file)
                return false
              }}
            >
              <Button icon={<UploadOutlined />} loading={upload.isPending}>
                {t('stakeholder.attachments.upload')}
              </Button>
            </Upload>
          </Space>
        </Can>
      }
    >
      <Table
        size="small"
        rowKey="id"
        loading={attachments.isPending}
        pagination={false}
        dataSource={[...(attachments.data ?? [])]}
        locale={{ emptyText: t('stakeholder.attachments.empty') }}
        columns={[
          { key: 'kind', title: t('stakeholder.attachments.kind'), render: (_, row) => t(`stakeholder.attachmentKind.${row.kind}`) },
          {
            key: 'file',
            title: t('stakeholder.attachments.file'),
            render: (_, row) => (
              <Button type="link" icon={<PaperClipOutlined />} onClick={() => void open(row.id)}>
                {row.fileName ?? t('stakeholder.attachments.unnamed')}
              </Button>
            ),
          },
          { key: 'at', title: t('stakeholder.attachments.at'), render: (_, row) => formatDateTime(row.createdAt) },
          {
            key: 'remove',
            title: t('common.actions'),
            width: 80,
            render: (_, row) => (
              <Can permission="stakeholder.manage">
                <Popconfirm title={t('stakeholder.attachments.removeConfirm')} onConfirm={() => remove.mutate(row.id)}>
                  <Button type="text" danger icon={<DeleteOutlined />} aria-label={t('stakeholder.attachments.remove')} />
                </Popconfirm>
              </Can>
            ),
          },
        ]}
      />
    </Card>
  )
}
